import type { ApAgentRun } from "@/accounts-payable/types";
import { dayjs } from "@/core/utils/date";
import { isPendingReviewAction } from "./action-review";
import { inZone } from "./format";

/**
 * When the ServiceTitan data a run's proposals rest on is from: when the run
 * read it, or a later re-check that found nothing had changed.
 */
export const stDataAsOf = (run: ApAgentRun): string =>
  run.st_verified_at && dayjs(run.st_verified_at).isAfter(run.st_data_as_of)
    ? run.st_verified_at
    : run.st_data_as_of;

/**
 * Whether a run's open proposals rest on ServiceTitan data older than the
 * business's `ap_stale_after_hours`. ServiceTitan may have changed since (a PO
 * received, an item added), so a proposal can look wrong without the agent
 * having been wrong. Only a run with something left to review that rests on
 * recorded ServiceTitan facts is stale: a re-check can clear those, while a
 * flag or a job match has nothing to re-check.
 */
export const isStaleRun = (
  run: ApAgentRun | undefined,
  staleAfterHours: number | undefined,
  now: dayjs.Dayjs = dayjs(),
): boolean => {
  if (!run || !staleAfterHours || run.superseded) return false;
  if (!run.actions.some((a) => isPendingReviewAction(a) && a.st_checkable))
    return false;
  return now.diff(dayjs(stDataAsOf(run)), "hour", true) >= staleAfterHours;
};

/**
 * The warning above a run's open proposals, or null when none is due: they
 * rest on values a re-check found changed in ServiceTitan, or on data older
 * than the business's threshold. A superseded run's proposals can't be
 * approved, so they need none.
 */
export const staleNoticeCopy = (
  run: ApAgentRun,
  staleAfterHours: number | undefined,
  timeZone?: string,
  now: dayjs.Dayjs = dayjs(),
): { title: string; detail: string } | null => {
  if (run.superseded) return null;
  const pending = run.actions.filter(isPendingReviewAction);
  const changed = pending.filter((a) => (a.st_changes ?? []).length > 0);
  if (changed.length > 0) {
    const count = changed.length;
    // Some can still be approved against the current values; a blocked one
    // can't, whatever the reviewer acknowledges.
    const approvable = changed.some((a) => !a.approval_blocked_reason);
    return {
      title: "ServiceTitan has changed",
      detail: `${count === 1 ? "1 proposal rests" : `${count} proposals rest`} on values that changed since the agent proposed. ${
        approvable
          ? "Approve against the current values, or re-run the agent."
          : "Re-run the agent for proposals that fit ServiceTitan as it is now."
      }`,
    };
  }
  if (!isStaleRun(run, staleAfterHours, now)) return null;

  return {
    title: "Stale proposals",
    detail: `${staleDataCopy(stDataAsOf(run), timeZone, now)}. ServiceTitan may have changed since.`,
  };
};

/** Open proposals a re-check found resting on values that changed. */
const changedPendingActions = (run: ApAgentRun) =>
  run.actions.filter(
    (a) => isPendingReviewAction(a) && (a.st_changes ?? []).length > 0,
  );

/**
 * Whether ServiceTitan would refuse every open proposal a re-check found
 * changed (a PO received or canceled since), so nothing is left to approve
 * against the current values and re-running the agent is the only way on.
 */
export const allChangesBlocked = (run: ApAgentRun): boolean => {
  const changed = changedPendingActions(run);
  return (
    changed.length > 0 && changed.every((a) => !!a.approval_blocked_reason)
  );
};

/**
 * Which action the stale warning leads with; the other stays one click away.
 * Re-running the agent once a recent check found changes, and checking
 * ServiceTitan otherwise: before any check, or when the check that found the
 * changes is itself older than the business's `ap_stale_after_hours`.
 */
export const staleNoticePrimaryAction = (
  run: ApAgentRun,
  staleAfterHours: number | undefined,
  now: dayjs.Dayjs = dayjs(),
): "check" | "rerun" => {
  const checkedAt = changedPendingActions(run)
    .map((a) => a.st_checked_at)
    .filter((at): at is string => !!at)
    .sort()
    .at(-1);
  if (!checkedAt) return "check";
  if (
    staleAfterHours &&
    now.diff(dayjs(checkedAt), "hour", true) >= staleAfterHours
  )
    return "check";
  return "rerun";
};

/**
 * "Calculated from ServiceTitan data as of Mon, Oct 20, 3:15 PM (2 days ago)",
 * in the business's time zone. The year shows once it isn't this year.
 */
export const staleDataCopy = (
  asOf: string,
  timeZone?: string,
  now: dayjs.Dayjs = dayjs(),
): string => {
  const at = inZone(dayjs(asOf), timeZone);
  const format =
    at.year() === inZone(now, timeZone).year()
      ? "ddd, MMM D, h:mm A"
      : "ddd, MMM D, YYYY, h:mm A";
  return `Calculated from ServiceTitan data as of ${at.format(format)} (${dayjs(asOf).from(now)})`;
};
